import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deactivateCompanyEmployee,
  getCompanyEmployees,
  inviteCompanyEmployee,
  resendCompanyEmployeeInvite,
  updateCompanyEmployee,
  InviteCompanyEmployeeRequestDto,
  UpdateCompanyEmployeeRequestDto,
} from '@/shared/api/companies';
import { useAppQuery } from '@/shared/lib/useAppQuery';

const employeesQueryKey = (companyId: string) =>
  ['company', companyId, 'employees'] as const;

export function useEmployees(companyId?: string) {
  return useAppQuery({
    queryKey: companyId ? employeesQueryKey(companyId) : ['company', 'employees', 'disabled'],
    queryFn: () => getCompanyEmployees(companyId!),
    enabled: !!companyId,
    customError: true,
  });
}

export function useInviteEmployee(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteCompanyEmployeeRequestDto) =>
      inviteCompanyEmployee(companyId, data),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: employeesQueryKey(companyId) });
    },
  });
}

export function useUpdateEmployee(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateCompanyEmployeeRequestDto;
    }) => updateCompanyEmployee(companyId, userId, data),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: employeesQueryKey(companyId) });
    },
  });
}

export function useDeactivateEmployee(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => deactivateCompanyEmployee(companyId, userId),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: employeesQueryKey(companyId) });
    },
  });
}

export function useResendEmployeeInvite(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => resendCompanyEmployeeInvite(companyId, userId),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: employeesQueryKey(companyId) });
    },
  });
}
